import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useDevice } from '@bonnet/next/device';

import { formatNumber } from '@atc/string-fns';

import { LoadingIndicator, Select, SideBarToModal, Text } from 'reaxl';

import formatReviewRatings from '@/utilities/formatReviewRatings';

import numRecordsOptions from '@/config/consumerRatingRecords';
import consumerRatingSortOptions from '@/config/consumerRatingSort';

import vdpRatingReviewDuck from '@/ducks/vdp/vdpRatingReviewDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';
import vdpReviewPaginationDuck from '@/ducks/vdp/vdpReviewPaginationDuck.js';

import kbbReviewsFetcher from '@/fetchers/vdp/kbbReviewsFetcher';

import ConsumerReview from './ConsumerReview';
import ConsumerReviewPagination from './ConsumerReviewPagination';

function ConsumerReviewModal({
    ...rest
}) {
    const device = useDevice();
    const isXs = device?.is?.xs || false;
    const dispatch = useDispatch();
    const { consumerRatings, showReviewsModal, scrollToTopModal } = useSelector(vdpRatingReviewDuck.selectors.getDuckState);
    const { kbbVehicleId, make, model, year } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const { currentPage, numRecords } = useSelector(vdpReviewPaginationDuck.selectors.getDuckState);

    const titleRef = useRef();

    const [sortType, setSortType] = useState(consumerRatingSortOptions?.options[0].value);
    const [isLoading, setIsLoading] = useState(false);
    const [displayReviewList, setDisplayReviewList] = useState([]);

    const updateReviewList = useCallback(async (query) => {
        setIsLoading(true);
        const response = await kbbReviewsFetcher(kbbVehicleId, { target: 'consumerReview', query });

        if (response?.results?.length > 0) {
            const sortedReviewsList = response.results.map((item) => ({ ...item, ratings: formatReviewRatings(item, false) }));
            setDisplayReviewList(sortedReviewsList);
        }

        setIsLoading(false);
    }, [kbbVehicleId]);

    useEffect(() => {
        const query = {
            perPage: numRecords,
            page: currentPage,
            sort: sortType,
        };
        updateReviewList(query);
    }, [currentPage, numRecords, sortType, updateReviewList]);

    useEffect(() => {
        if (scrollToTopModal) {
            titleRef.current.scrollIntoView();
            dispatch(vdpRatingReviewDuck.creators.setScrollToTopModal(false));
        }
    }, [dispatch, scrollToTopModal]);

    const hideModal = useCallback(() => {
        dispatch(vdpRatingReviewDuck.creators.setShowReviewsModal(false));
        // reset sort to default when closing modal
        setSortType(consumerRatingSortOptions?.options[0].value);
        // reset first record and numRecords to initial state
        dispatch(vdpReviewPaginationDuck.creators.deriveCurrentPage({}));
    }, [dispatch]);

    const onSortTypeChange = (event) => {
        setSortType(event.target.value);
        dispatch(vdpReviewPaginationDuck.creators.resetPagination());
    };

    const onNumRecordsChange = (event) => {
        dispatch(vdpReviewPaginationDuck.creators.setNumRecords(event.target.value));
    };

    const bodyContent = (
        // padding needed to stop horizontal scroll on desktop
        (
            <div
                data-cmp="cntnr-consumerReview-modal"
                className="padding-horizontal-sm-3"
            >
                <div
                    className="display-flex flex-wrap justify-content-between align-items-center gap-4 margin-top-3 margin-top-sm-0 margin-bottom-5"
                    data-cmp="cntnr-consumerReview-desc"
                    ref={titleRef}
                >
                    <Text weight="bold">
                        {`${formatNumber({ value: consumerRatings?.totalReviewCount })} Reviews`}
                    </Text>
                    <div className="display-flex gap-4">
                        {/* Sort options */}
                        <Select
                            className="inside-select-label margin-bottom-0 position-relative"
                            onChange={onSortTypeChange}
                            value={sortType}
                            {...consumerRatingSortOptions}

                        />
                        {/* Number of records options */}
                        <Select
                            className="margin-bottom-0"
                            data-cmp="sel-numRecords"
                            onChange={onNumRecordsChange}
                            selectClassName="padding-right-5 text-size-300" // something is wrong in the design system
                            value={numRecords}
                            {...numRecordsOptions}
                        />
                    </div>
                </div>
                <LoadingIndicator show={!isLoading}>
                    {displayReviewList.map((consumerReview, index) => (
                        <ConsumerReview
                            consumerReview={consumerReview}
                            key={`consumer-review-${index}`}
                            isModal
                        />
                    ))}
                </LoadingIndicator>
                <ConsumerReviewPagination />
            </div>
        )
    );

    return (
        <SideBarToModal
            collapsed={isXs}
            title={`${year} ${make?.name ?? make} ${model?.name ?? model} Consumer Reviews`}
            content={bodyContent}
            sideBarOptions={{
                fullWidth: true,
            }}
            modalOptions={{
                scrollPanel: true,
                bsSize: 'large',
            }}
            onHide={hideModal}
            show={showReviewsModal}
            {...rest}
        />
    );
}

export default memo(ConsumerReviewModal);
